import {useTranslation} from "react-i18next";
import {useMemo} from "react";
import {yup} from "./index";


export const useModel23Schema = () => {
    const {t} = useTranslation('validation');

    const number = yup.number().nullable().typeError(t('validation:number'))
    const positiveNumber = number.min(0, t('validation:gte', {gte: 0}));
    const positiveInteger = positiveNumber.integer(t('validation:integer'));
    const temperature = number.lessThan(50, t('validation:lt', {lt: 50}))

    const requiredString = yup.string().required(t('validation:required'));
    const requiredPositiveNumber = positiveNumber.required(t('validation:required'));
    const requiredPositiveInteger = positiveInteger.required(t('validation:required'));

    const oneOfSchema = (values) => yup.mixed().nullable().oneOf(values, t('validation:oneOf', {values: values.join(' ')}));

    const valWithNoFracture = (noFractureField, noFractureCode, fieldSchema, fieldSchemaNoFracture) => {
        return yup.string().when(noFractureField, {
            is: noFractureCode,
            then: () => fieldSchemaNoFracture,
            otherwise: () => fieldSchema,
        })
    };


    return useMemo(
        () => {
            const hsLabel = 'HS'

            return yup.object().shape({
                metadata: yup.object().shape({
                    datetime: yup.date().required(t('validation:required')).typeError(t('validation:date')),
                    station: yup.object().required(t('validation:required')),
                    lat: yup.number().min(-90, t('validation:gte', {gte: -90})).max(90, t('validation:lte', {lte: 90})).required(t('validation:required')).typeError(t('validation:number')),
                    lng: yup.number().min(-180, t('validation:gte', {gte: -180})).max(180, t('validation:lte', {lte: 180})).required(t('validation:required')).typeError(t('validation:number')),
                    elevation: positiveNumber,
                    aspect: yup.string().emptyToNull(),
                    slope: positiveNumber,
                }),
                snowWeatherData: yup.object().shape({
                    hs: positiveInteger.required(t('validation:required')),
                    ta: temperature
                }),
                stratigraphicProfile: yup.array().of(
                    yup.object().shape({
                        top: positiveInteger.required(t('validation:required')).max(
                            yup.ref('$hs'), t('validation:lte', {lte: hsLabel})
                        ),
                        bottom: positiveInteger.required(t('validation:required')).lessThan(
                            yup.ref('top'),
                            t('validation:lte', {lte: 'top'})
                        ),
                        grainShapePrimary: yup.string().required(t('validation:required')),
                        grainShapeSecondary: yup.string().required(t('validation:required')),
                        grainSizeAvg: positiveNumber,
                        grainSizeMax: positiveNumber,
                    })
                ),
                ramTests: yup.array().of(
                    yup.object().shape({
                        measurements: yup.array().of(
                            yup.object().shape({
                                dropHeight: positiveInteger.required(t('validation:required')),
                                weightTube: positiveNumber.required(t('validation:required')),
                                weightHammer: positiveNumber.required(t('validation:required')),
                                dropsNumber: positiveInteger.required(t('validation:required')),
                                deltaHeight: positiveNumber.required(t('validation:required')),
                                depth: positiveInteger.required(t('validation:required')).max(
                                    yup.ref('$hs'), t('validation:lte', {lte: hsLabel})
                                ),
                                hardness: positiveNumber.required(t('validation:required')),
                            })
                        )
                    })
                ),
                temperatureProfiles: yup.array().of(
                    yup.object().shape({
                        measurements: yup.array().of(
                            yup.object().shape({
                                depth: positiveInteger.required(t('validation:required')).max(
                                    yup.ref('$hs'), t('validation:lte', {lte: hsLabel})
                                ),
                                temperature: temperature.required(t('validation:required')),
                            })
                        )
                    })
                ),
                densityProfiles: yup.array().of(
                    yup.object().shape({
                        measurements: yup.array().of(
                            yup.object().shape({
                                top: positiveInteger.max(
                                    yup.ref('$hs'), t('validation:lte', {lte: hsLabel})
                                ),
                                bottom: positiveInteger.lessThan(
                                    yup.ref('top'),
                                    t('validation:lte', {lte: 'top'})
                                ),
                                depth: positiveInteger.max(
                                    yup.ref('top'), t('validation:lte', {lte: 'top'})
                                ).min(
                                    yup.ref('bottom'), t('validation:gte', {gte: 'bottom'})
                                ),
                                density: positiveNumber.required(t('validation:required')),
                                fillHeight: positiveInteger,
                                weight: positiveNumber,
                            })
                        )
                    })
                ),
                compressionTests: yup.array().of(
                    yup.object().shape({
                        measurements: yup.array().of(
                            yup.object().shape({
                                ectCode: requiredString,
                                fractureType: valWithNoFracture('ectCode', 'X', requiredString, oneOfSchema(['X', null])),
                                qualityShear: valWithNoFracture('ectCode', 'X', requiredString, oneOfSchema(['X', null])),
                                depth: valWithNoFracture('ectCode', 'X', requiredPositiveInteger, positiveInteger),
                                score: valWithNoFracture('ectCode', 'X', requiredPositiveNumber, oneOfSchema([31, null])),
                                tdh: positiveInteger,
                            })
                        )
                    })
                ),
                extendedColumnTests: yup.array().of(
                    yup.object().shape({
                        measurements: yup.array().of(
                            yup.object().shape({
                                ectCode: requiredString,
                                fractureType: valWithNoFracture('ectCode', 'X', requiredString, oneOfSchema(['X', null])),
                                qualityShear: valWithNoFracture('ectCode', 'X', requiredString, oneOfSchema(['X', null])),
                                depth: valWithNoFracture('ectCode', 'X', requiredPositiveInteger, positiveInteger),
                                score: valWithNoFracture('ectCode', 'X', requiredPositiveNumber, oneOfSchema([31, null])),
                                tdh: positiveInteger,
                            })
                        )
                    })
                ),
                rutschBlockTests: yup.array().of(
                    yup.object().shape({
                        measurements: yup.array().of(
                            yup.object().shape({
                                rbCode: requiredString,
                                fractureType: valWithNoFracture('rbCode', 'X', requiredString, oneOfSchema(['X', null])),
                                qualityShear: valWithNoFracture('rbCode', 'X', requiredString, oneOfSchema(['X', null])),
                                depth: valWithNoFracture('rbCode', 'X', requiredPositiveInteger, positiveInteger),
                                score: valWithNoFracture('rbCode', 'X', requiredPositiveNumber, oneOfSchema([7, null])),
                                tdh: positiveInteger,
                            })
                        )
                    })
                ),
                propagationSawTests: yup.array().of(
                    yup.object().shape({
                        pstCode: yup.string().required(t('validation:required')),
                        fractureType: valWithNoFracture('pstCode', 'X', requiredString, oneOfSchema(['X', null])),
                        depth: requiredPositiveInteger,
                        cutLength: requiredPositiveNumber,
                        columnLength: requiredPositiveNumber,
                        tdh: positiveInteger,
                    })
                ),
            })
        },
        [t]
    );
}